import { Button } from "@/components/ui/button";
import { ExcursionCard } from "@/components/ExcursionCard";
import { TestimonialCard } from "@/components/TestimonialCard";
import { useExcursions } from "@/hooks/useExcursions";
import { useTestimonials } from "@/hooks/useTestimonials";
import { useSiteImages } from "@/hooks/useSiteImages";
import { ArrowRight, MessageCircle, Star } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo } from "react";
import { SmartImage } from "@/components/SmartImage";
import { ScrollReveal } from "@/components/ScrollReveal";
import { HomeHowItWorks } from "@/components/HomeHowItWorks";
import { HomeFAQ } from "@/components/HomeFAQ";
import { HomeSocialSection } from "@/components/HomeSocialSection";
import { HomeTrustBar } from "@/components/HomeTrustBar";
import { CountUp } from "@/components/motion/CountUp";
import { MagneticButton } from "@/components/motion/MagneticButton";
import { ContactWizard } from "@/components/contact/ContactWizard";
import { FadeIn } from "@/components/motion/FadeIn";
import { Parallax } from "@/components/motion/Parallax";
import { Clock, ShieldCheck, Sparkles, Footprints } from "lucide-react";
import { AssoPhotoCarousel } from "@/components/AssoPhotoCarousel";

export const HomepagePreview = () => {
  const { testimonials } = useTestimonials(false);
  const { excursions } = useExcursions();
  const { images: siteImages } = useSiteImages();

  const published = excursions.filter((e) => e.is_published);
  const featuredExcursions = (() => {
    const featured = published.filter((e) => e.tags?.coup_de_coeur);
    return (featured.length >= 3 ? featured : published).slice(0, 6);
  })();

  const topRatedTestimonials = [...testimonials].sort((a, b) => b.rating - a.rating).slice(0, 3);
  const ratingStats = useMemo(() => {
    if (!testimonials.length) return { avg: 0, count: 0 };
    const sum = testimonials.reduce((s, t) => s + t.rating, 0);
    return { avg: sum / testimonials.length, count: testimonials.length };
  }, [testimonials]);

  const cleaPortrait = siteImages['clea-portrait-homepage']?.image_url || "/clea-profile.webp";
  const rioImage = siteImages['homepage-rio']?.image_url || "/hero-home.webp";

  return (
    <div>
      {/* Section 1 · Votre guide locale francophone à Rio */}
      <section id="presentation-section" className="relative py-14 md:py-36 overflow-hidden">
        {/* Numéro de chapitre en filigrane */}
        <div aria-hidden className="absolute -top-6 right-2 md:right-12 font-league text-[7rem] md:text-[20rem] leading-none text-roca-orange/[0.06] select-none pointer-events-none">
          01
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <FadeIn direction="left" distance={40} className="lg:col-span-5 order-2 lg:order-1">
              <Parallax speed={0.1} className="relative inline-block w-full">
                {/* Cadre offset décoratif */}
                <div aria-hidden className="absolute -top-4 -left-4 w-full h-full border-2 border-roca-orange rounded-md" />
                <div className="relative overflow-hidden rounded-md image-reveal max-h-[70vh] mx-auto">
                  <SmartImage
                    src={cleaPortrait}
                    alt="Cléa Carioca · guide francophone à Rio de Janeiro"
                    aspectClassName="aspect-[3/4]"
                    sizes="(max-width: 1024px) 100vw, 40vw"
                  />
                </div>
              </Parallax>
            </FadeIn>
            <FadeIn direction="right" distance={40} delay={120} className="lg:col-span-7 order-1 lg:order-2 space-y-7">
              <div className="flex items-center gap-3">
                <span className="font-league text-xl text-roca-orange">01</span>
                <span className="h-px w-12 bg-roca-orange" />
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Le guide</p>
              </div>
              <h2 className="font-league uppercase tracking-tight text-3xl sm:text-5xl md:text-6xl text-foreground leading-[0.95]">
                Votre guide locale <span className="text-gradient-roca">francophone</span> à Rio
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Je suis Cléa, française installée à Rio de Janeiro depuis plusieurs années. Guide certifiée et passionnée, je vous accompagne pour vivre des expériences locales, immersives et sereines, loin des circuits touristiques classiques.
              </p>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                Avec moi, vous découvrez le Rio que j'aime : ses randonnées secrètes, ses quartiers vibrants, ses rencontres chaleureuses. Un Rio plus humain, plus vrai, plus engagé.
              </p>
              <Link to="/a-propos" className="group inline-flex items-center gap-3 text-foreground font-medium hover:text-roca-orange transition-colors">
                Découvrir mon parcours
                <span className="w-8 h-px bg-current group-hover:w-16 transition-all duration-500" />
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Section 2 · Vivez Rio autrement */}
      <section className="relative py-14 md:py-36 bg-muted/30 overflow-hidden">
        <div aria-hidden className="absolute -top-6 right-2 md:right-12 font-league text-[7rem] md:text-[20rem] leading-none text-roca-yellow/[0.10] select-none pointer-events-none">
          02
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative">
          {/* En-tête éditorial centré */}
          <FadeIn className="mb-12 md:mb-16 max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-5">
              <span className="h-px w-12 bg-roca-orange" />
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Chapitre 02 · Expérience</p>
              <span className="h-px w-12 bg-roca-orange" />
            </div>
            <h2 className="font-league uppercase tracking-tight text-4xl sm:text-6xl md:text-7xl text-foreground leading-[0.95]">
              Vivez Rio <span className="text-gradient-roca">autrement</span>
            </h2>
          </FadeIn>

          {/* Image panoramique pleine largeur */}
          <FadeIn direction="up" distance={30}>
            <Parallax speed={0.06}>
              <div className="overflow-hidden rounded-md image-reveal group mb-12 md:mb-16">
                <SmartImage
                  src={rioImage}
                  alt="Vivre Rio autrement · paysage carioca"
                  aspectClassName="aspect-[16/10] md:aspect-[24/9]"
                  sizes="100vw"
                  className="transition-transform duration-[1500ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
            </Parallax>
          </FadeIn>

          {/* Texte façon magazine · pull quote géant + 2 colonnes */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            <FadeIn direction="left" distance={30} className="lg:col-span-5">
              <p className="font-league uppercase text-3xl md:text-4xl text-foreground leading-[1.05]">
                Plus qu'une visite,<br />
                une <span className="text-gradient-roca">immersion</span> dans le quotidien et l'énergie de la ville.
              </p>
            </FadeIn>

            <FadeIn direction="right" distance={30} delay={120} className="lg:col-span-7 lg:columns-2 lg:gap-10 space-y-5 lg:space-y-0">
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed lg:break-inside-avoid lg:mb-5">
                Chaque expérience est pensée pour vous faire découvrir un Rio plus authentique, plus humain, plus vrai. Des rencontres, des histoires, des lieux que l'on ne trouve pas dans les guides… et surtout, une manière de voyager plus proche de la réalité locale.
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed lg:break-inside-avoid">
                Entre exploration de quartiers vivants et expériences uniques comme l'ascension du Pain de Sucre, chaque moment est pensé pour vous faire vivre Rio intensément.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Section 3 · Les expériences à ne pas manquer (grille cohérente) */}
      <section id="excursions-section" className="relative py-14 md:py-36 overflow-hidden">
        <div aria-hidden className="absolute -top-6 right-2 md:right-12 font-league text-[7rem] md:text-[20rem] leading-none text-roca-orange/[0.06] select-none pointer-events-none">
          03
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <FadeIn className="mb-12 md:mb-16 max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-league text-xl text-roca-orange">03</span>
              <span className="h-px w-12 bg-roca-orange" />
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Excursions</p>
            </div>
            <h2 className="font-league uppercase text-3xl sm:text-5xl md:text-6xl text-foreground leading-[0.95] mb-4">
              Les <span className="text-gradient-roca">expériences</span> à ne pas manquer
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground">
              {published.length > 0 ? `${published.length} manières de découvrir Rio à mes côtés.` : "Six manières de découvrir Rio à mes côtés."}
            </p>
          </FadeIn>

          <div className="grid gap-5 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {featuredExcursions.map((excursion, i) => (
              <FadeIn key={excursion.id} delay={(i % 3) * 100} direction="up" distance={30}>
                <div className="card-lift h-full">
                  <ExcursionCard
                    id={excursion.id}
                    title={excursion.title}
                    cover_image={excursion.cover_image}
                    image={excursion.images?.[0]}
                    tags={excursion.tags}
                  />
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={150}>
            <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Button asChild size="lg" className="rounded-full px-8 group">
                <Link to="/excursions">
                  Voir toutes les expériences
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <span className="text-sm text-muted-foreground">
                Et si une envie particulière vous trotte en tête, écrivez-moi directement.
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Section 4 · Comment ça se passe ? */}
      <HomeHowItWorks />

      {/* Section 5 · Un voyage qui a du sens */}
      <section className="relative py-14 md:py-36 bg-foreground text-background overflow-hidden">
        <div aria-hidden className="absolute -top-6 left-2 md:left-12 font-league text-[7rem] md:text-[20rem] leading-none text-background/[0.05] select-none pointer-events-none">
          05
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl relative">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            <FadeIn direction="left" className="lg:col-span-7 space-y-6 order-2 lg:order-1">
              <div className="flex items-center gap-3">
                <span className="font-league text-xl text-roca-orange">05</span>
                <span className="h-px w-12 bg-roca-orange" />
                <p className="text-xs uppercase tracking-[0.3em] text-background/60">Engagement</p>
              </div>
              <h2 className="font-league uppercase text-3xl sm:text-5xl md:text-6xl leading-[0.95]">
                Un voyage qui a du <span className="text-gradient-roca">sens</span>
              </h2>
              <p className="text-base sm:text-lg text-background/80 leading-relaxed text-justify">
                J'ai créé l'association <strong className="text-background">Meninos do Brasil</strong> et, depuis 2018, je soutiens des communautés locales à travers des actions concrètes&nbsp;: activités éducatives et sportives pour les enfants, accompagnement des familles pour répondre à leurs besoins essentiels.
              </p>
              <p className="text-base sm:text-lg text-background/80 leading-relaxed text-justify">
                Certaines de mes expériences sont à impact solidaire&nbsp;: pour chaque réservation, <strong className="text-background">5€ sont reversés</strong> afin de financer ces actions sur le terrain. Voyager avec moi, c'est vivre une expérience unique tout en ayant un impact concret auprès de la population locale.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pt-2">
                <Button asChild size="lg" className="bg-roca-orange hover:bg-roca-orange/90 text-white rounded-full px-8">
                  <Link to="/a-propos">Découvrir le projet</Link>
                </Button>
                <Link to="/excursions" className="text-sm text-background/70 hover:text-background underline underline-offset-4">
                  Voir les excursions solidaires
                </Link>
              </div>
            </FadeIn>
            {/* Colonne carrousel photos de l'association */}
            <FadeIn direction="right" delay={150} className="lg:col-span-5 order-1 lg:order-2">
              <AssoPhotoCarousel />
              <p className="mt-4 text-xs uppercase tracking-[0.2em] text-background/60 text-center">
                Meninos do Brasil · Sur le terrain
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Section 6 · Avis avec note moyenne XL */}
      <section id="testimonials-section" className="relative py-14 md:py-36 overflow-hidden">
        <div aria-hidden className="absolute -top-6 right-2 md:right-12 font-league text-[7rem] md:text-[20rem] leading-none text-roca-orange/[0.06] select-none pointer-events-none">
          06
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <FadeIn className="mb-10 md:mb-14">
            <div className="flex items-center gap-3 mb-4">
              <span className="font-league text-xl text-roca-orange">06</span>
              <span className="h-px w-12 bg-roca-orange" />
              <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Leurs retours d'expérience</p>
            </div>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5">
              <div>
                <h2 className="font-league uppercase text-3xl sm:text-5xl md:text-6xl text-foreground leading-[0.95] mb-4">
                  Ils ont découvert Rio à mes <span className="text-gradient-roca">côtés</span>
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground">Voici leurs mots.</p>
              </div>
              {ratingStats.count > 0 && (
                <div className="self-start md:self-auto flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-2.5 sm:py-3 rounded-2xl bg-background border border-border/50 shadow-sm">
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`w-5 h-5 ${i < Math.round(ratingStats.avg) ? 'fill-roca-orange text-roca-orange' : 'text-muted-foreground/30'}`}
                      />
                    ))}
                  </div>
                  <span className="text-3xl font-league text-foreground leading-none">
                    <CountUp to={ratingStats.avg} decimals={1} duration={1400} />
                  </span>
                  <span className="text-sm text-muted-foreground">
                    /5 · <CountUp to={ratingStats.count} /> avis
                  </span>
                </div>
              )}
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {topRatedTestimonials.map((t, i) => (
              <FadeIn key={t.id} delay={i * 120} direction="up" distance={28}>
                <div className="card-lift h-full">
                  <TestimonialCard {...t} compact />
                </div>
              </FadeIn>
            ))}
          </div>

          <FadeIn delay={300}>
            <div className="mt-10">
              <Link to="/avis" className="group inline-flex items-center gap-3 text-foreground font-medium hover:text-roca-orange transition-colors">
                Lire tous les avis
                <span className="w-8 h-px bg-current group-hover:w-12 transition-all" />
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Section · Réseaux sociaux */}
      <HomeSocialSection />

      {/* Section 7 · Mini-FAQ */}
      <HomeFAQ />

      {/* CTA Final · double choix */}
      {/* CTA Final · tunnel + WhatsApp pour maximiser conversion */}
      <section id="reserver" className="relative py-14 md:py-32 bg-muted/30 overflow-hidden">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-roca-orange/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative">
          <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-start">
            {/* Colonne gauche · pitch + WhatsApp + réassurance */}
            <ScrollReveal className="lg:sticky lg:top-28 space-y-8">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-3">Réservation</p>
                <div className="w-12 h-px bg-roca-orange mb-6" />
                <h2 className="font-league uppercase text-3xl sm:text-5xl md:text-6xl text-foreground leading-[0.95] mb-5">
                  Prêt·e à explorer <span className="text-gradient-roca">Rio</span> ?
                </h2>
                <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                  Construisez votre demande en 4 étapes guidées, ou écrivez-moi directement sur WhatsApp. Réponse personnalisée sous 24h.
                </p>
              </div>

              {/* WhatsApp shortcut */}
              <div className="rounded-2xl bg-foreground text-background p-6 sm:p-8 shadow-float">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-lg mb-1">Vous préférez l'instantané ?</p>
                    <p className="text-background/70 text-sm mb-4">
                      Échangez avec Cléa en direct sur WhatsApp.
                    </p>
                    <MagneticButton>
                      <Button asChild size="lg" className="bg-green-600 hover:bg-green-700 text-white rounded-full px-6 h-11 shadow-lg">
                        <a href="https://wa.me/5521976795050?text=Bonjour%20Cléa%2C%20je%20souhaite%20en%20savoir%20plus%20sur%20vos%20excursions%20!" target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="w-4 h-4 mr-2" />
                          Écrire à Cléa
                        </a>
                      </Button>
                    </MagneticButton>
                  </div>
                </div>
              </div>

              {/* Réassurance */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { icon: Clock, label: "Réponse 24h" },
                  { icon: ShieldCheck, label: "Sans engagement" },
                  { icon: Sparkles, label: "Sur-mesure" },
                ].map(({ icon: Icon, label }) => (
                  <div key={label} className="flex flex-col items-center text-center gap-2 p-3 rounded-xl bg-background/60 border border-border/40">
                    <Icon className="w-5 h-5 text-roca-orange" />
                    <span className="text-xs font-medium text-foreground">{label}</span>
                  </div>
                ))}
              </div>
            </ScrollReveal>

            {/* Colonne droite · Tunnel formulaire */}
            <ScrollReveal delay={150}>
              <ContactWizard />
              <p className="text-center text-xs text-muted-foreground mt-4">
                Vos informations sont confidentielles et ne servent qu'à vous répondre.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
};
